import type { IncomeDraft } from "Forms/Schemas/Budget/Incomes/incomeBulkFormSchema";
import {
  AmountFrequency,
  type BudgetBatchItemError,
  type BudgetBatchItemErrorCode,
  type Income,
  IncomeAmountAmountType,
  type IncomeBody,
  IncomeIncomeType,
  type IncomesBatchBody,
} from "Infrastructure/ApiZfp/Schema";
import { parseFormNumber } from "../parseFormNumber";

const DEFAULT_AMOUNT_TYPE: Record<IncomeIncomeType, IncomeAmountAmountType> = {
  [IncomeIncomeType.employment]: IncomeAmountAmountType.employment,
  [IncomeIncomeType.self_employment]: IncomeAmountAmountType.self_employment,
  [IncomeIncomeType.rental]: IncomeAmountAmountType.rental,
  [IncomeIncomeType.parental]: IncomeAmountAmountType.parental,
  [IncomeIncomeType.pension]: IncomeAmountAmountType.pension,
  [IncomeIncomeType.disability]: IncomeAmountAmountType.first_degree_disability,
  [IncomeIncomeType.orphan_or_widow_pension]: IncomeAmountAmountType.orphan_pension,
  [IncomeIncomeType.other]: IncomeAmountAmountType.other,
};

export const createIncomeDraft = (incomeType: IncomeIncomeType, clientGuid = ""): IncomeDraft => ({
  incomeType,
  clientGuid,
  amountType: DEFAULT_AMOUNT_TYPE[incomeType],
  amountValue: "",
  name: "",
});

/**
 * One draft per saved income. An income with several amounts (the old form allowed that for the other income)
 * becomes one read-only draft per amount, because a draft holds a single amount.
 */
export const toIncomeDrafts = (income: Income): IncomeDraft[] => {
  const saved = { guid: income.guid, incomeType: income.incomeType, clientGuid: income.clientGuid };

  if (income.incomeAmounts.length > 1) {
    return income.incomeAmounts.map((incomeAmount) => ({
      ...saved,
      amountType: incomeAmount.amountType,
      amountValue: incomeAmount.amount.value.toString(),
      name: incomeAmount.name,
      isReadOnly: true,
    }));
  }

  const [incomeAmount] = income.incomeAmounts;
  return [
    {
      ...saved,
      amountType: incomeAmount?.amountType ?? DEFAULT_AMOUNT_TYPE[income.incomeType],
      amountValue: incomeAmount?.amount.value.toString() ?? "",
      name: incomeAmount?.name ?? "",
    },
  ];
};

/** Saved editable drafts by guid - what the drawer compares the current values against. */
export const getSavedIncomeDrafts = (incomes: Income[]): ReadonlyMap<string, IncomeDraft> =>
  new Map(
    incomes.flatMap((income) =>
      toIncomeDrafts(income)
        .filter((draft) => !draft.isReadOnly)
        .map((draft) => [income.guid, draft] as const),
    ),
  );

// Amounts are compared as numbers - the mask turns "35000" into "35 000" without changing the value.
const isIncomeDraftChanged = (draft: IncomeDraft, saved: IncomeDraft) =>
  draft.clientGuid !== saved.clientGuid ||
  draft.amountType !== saved.amountType ||
  draft.name.trim() !== saved.name.trim() ||
  parseFormNumber(draft.amountValue) !== parseFormNumber(saved.amountValue);

/** A new draft, or a saved one whose values differ from what is saved. */
export const isIncomeDraftUnsaved = (draft: IncomeDraft, savedDrafts: ReadonlyMap<string, IncomeDraft>) => {
  if (!draft.guid) {
    return true;
  }
  const saved = savedDrafts.get(draft.guid);
  return !draft.isReadOnly && saved !== undefined && isIncomeDraftChanged(draft, saved);
};

/**
 * The income type of every unsaved change in the drawer - a new draft, an edited saved one and a deleted saved one.
 * A new draft removed before saving is gone without a trace, a saved one edited back to its saved values is no change.
 */
export const getChangedIncomeTypes = (
  drafts: IncomeDraft[],
  savedDrafts: ReadonlyMap<string, IncomeDraft>,
  deletedGuids: string[],
): IncomeIncomeType[] => [
  ...drafts.filter((draft) => isIncomeDraftUnsaved(draft, savedDrafts)).map((draft) => draft.incomeType),
  ...deletedGuids.flatMap((guid) => savedDrafts.get(guid)?.incomeType ?? []),
];

const monthlyAmount = (raw: string) => ({
  value: parseFormNumber(raw),
  currencyCode: "CZK",
  frequency: AmountFrequency.monthly,
});

export const toIncomeBody = (draft: IncomeDraft): IncomeBody => ({
  incomeType: draft.incomeType,
  clientGuid: draft.clientGuid,
  incomeAmounts: [
    {
      amountType: draft.amountType,
      name: draft.incomeType === IncomeIncomeType.other ? draft.name.trim() : "",
      amount: monthlyAmount(draft.amountValue),
    },
  ],
});

export type IncomesBatch = {
  readonly body: Required<IncomesBatchBody>;
  /** Draft index of every `create` / `update` item, in the order they are in the body. */
  readonly createIndexes: number[];
  readonly updateIndexes: number[];
};

export const toIncomesBatch = (
  drafts: IncomeDraft[],
  savedDrafts: ReadonlyMap<string, IncomeDraft>,
  deletedGuids: string[],
): IncomesBatch => {
  const batch: IncomesBatch = { body: { create: [], update: [], delete: deletedGuids }, createIndexes: [], updateIndexes: [] };

  drafts.forEach((draft, index) => {
    if (!draft.guid) {
      batch.body.create.push(toIncomeBody(draft));
      batch.createIndexes.push(index);
    } else if (isIncomeDraftUnsaved(draft, savedDrafts)) {
      batch.body.update.push({ ...toIncomeBody(draft), guid: draft.guid });
      batch.updateIndexes.push(index);
    }
  });

  return batch;
};

export type IncomesBatchItemError =
  | { readonly kind: "draft"; readonly index: number; readonly code: BudgetBatchItemErrorCode }
  | { readonly kind: "delete"; readonly guid: string; readonly code: BudgetBatchItemErrorCode };

const BATCH_ERROR_PATH = /^(create|update|delete)\[(\d+)\]/;

/** Leads every item error of the batch (`path` like `create[1].clientGuid`) back to its draft or deleted guid. */
export const locateIncomesBatchErrors = (errors: BudgetBatchItemError[], batch: IncomesBatch): IncomesBatchItemError[] =>
  errors.flatMap(({ code, path }): IncomesBatchItemError[] => {
    const [, list, position] = BATCH_ERROR_PATH.exec(path) ?? [];
    const at = Number(position);

    if (list === "delete") {
      const guid = batch.body.delete[at];
      return guid ? [{ kind: "delete", guid, code }] : [];
    }

    const index = (list === "create" ? batch.createIndexes : list === "update" ? batch.updateIndexes : [])[at];
    return index === undefined ? [] : [{ kind: "draft", index, code }];
  });
